<template>
  <!--
    Bloque de acciones pendientes de control de stock para los dashboards.

    Se muestra solo si la empresa tiene contratado el módulo de inventario y el
    usuario tiene permiso de control de stock. Sin alertas no ocupa lugar: el
    dashboard queda como estaba.
  -->
  <div v-if="visible" class="alertas-stock">
    <div class="alertas-stock-header">
      <div class="alertas-stock-title">
        <v-icon small :color="colorSeveridad(severidadMaxima)" class="mr-2">
          mdi-alert-circle-outline
        </v-icon>
        Acciones pendientes
      </div>
      <v-btn text small class="text-none" @click="verTodo()">
        Ver todo
        <v-icon small right>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <div class="alertas-stock-grid">
      <v-card
        v-for="alerta in alertas"
        :key="alerta.tipo"
        flat
        class="alerta-card"
        :class="`alerta-card--${alerta.severidad}`"
        @click="verTodo(alerta)"
      >
        <div class="alerta-card-top">
          <v-icon small :color="colorSeveridad(alerta.severidad)">
            {{ iconoTipo(alerta.tipo) }}
          </v-icon>
          <span class="alerta-card-titulo">{{ alerta.titulo }}</span>
        </div>

        <div class="alerta-card-detalle">{{ alerta.detalle }}</div>

        <div v-if="alerta.items.length" class="alerta-card-items">
          <span
            v-for="item in alerta.items.slice(0, 3)"
            :key="item.psp_id"
            class="alerta-card-item"
          >
            {{ item.descripcion }}
          </span>
          <span v-if="alerta.cantidad > 3" class="alerta-card-item alerta-card-item--mas">
            +{{ alerta.cantidad - 3 }} más
          </span>
        </div>

        <div class="alerta-card-accion">
          {{ alerta.accion }}
          <v-icon x-small class="ml-1">mdi-arrow-right</v-icon>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import InventarioAPI from "@/api/inventario";
import inventarioMixin from "@/mixins/inventario.mixin";
import suscripcionMixin from "@/mixins/suscripcion.mixin";
import { ICONO_POR_TIPO, COLOR_POR_SEVERIDAD, ORDEN_SEVERIDAD } from "@/utils/alertasStock";

export default {
  name: "AlertasStockPanel",

  mixins: [inventarioMixin, suscripcionMixin],

  data() {
    return {
      alertas: [],
      cargando: false,
    };
  },

  computed: {
    puedeVerAlertas() {
      const tienePermiso = this.$store.getters["auth/tienePermiso"];
      return tienePermiso("realizar_inventario") || tienePermiso("modificar_stock");
    },

    habilitado() {
      return this.tieneModulo("inventario") && this.puedeVerAlertas;
    },

    visible() {
      return this.habilitado && !this.cargando && this.alertas.length > 0;
    },

    severidadMaxima() {
      if (!this.alertas.length) return "informativa";
      return this.alertas
        .map((a) => a.severidad)
        .sort((a, b) => ORDEN_SEVERIDAD[a] - ORDEN_SEVERIDAD[b])[0];
    },
  },

  methods: {
    iconoTipo(tipo) {
      return ICONO_POR_TIPO[tipo] || "mdi-alert-outline";
    },

    colorSeveridad(severidad) {
      return COLOR_POR_SEVERIDAD[severidad] || "grey";
    },

    verTodo(alerta = null) {
      const location = { name: this.rutaInventario("alertas") };
      if (alerta) location.query = { tipo: alerta.tipo };
      this.$router.push(location);
    },

    async fetchAlertas() {
      if (!this.habilitado) return;

      this.cargando = true;
      try {
        const params = {};
        if (this.uuidSucursalInventario) params.sucursal = this.uuidSucursalInventario;

        const { data } = await InventarioAPI.getAlertas(params);
        this.alertas = data.alertas;
      } catch (e) {
        // El dashboard no es el lugar para pelear con un error de alertas:
        // si no se pueden traer, el bloque simplemente no aparece.
        this.alertas = [];
      } finally {
        this.cargando = false;
      }
    },
  },

  created() {
    this.fetchAlertas();
  },
};
</script>

<style scoped>
.alertas-stock {
  margin-bottom: 16px;
}

.alertas-stock-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.alertas-stock-title {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #888;
}

.alertas-stock-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}

.alerta-card {
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-left-width: 3px;
  background: transparent !important;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.alerta-card:hover {
  background: rgba(128, 128, 128, 0.08) !important;
}

.alerta-card--critica {
  border-left-color: #e53935;
}

.alerta-card--advertencia {
  border-left-color: #fb8c00;
}

.alerta-card--informativa {
  border-left-color: #64b5f6;
}

.alerta-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.alerta-card-titulo {
  font-size: 0.95rem;
  font-weight: 600;
}

.alerta-card-detalle {
  font-size: 0.8rem;
  color: #8c8c8c;
  margin-top: 4px;
}

.alerta-card-items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
}

.alerta-card-item {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1px solid rgba(128, 128, 128, 0.3);
  color: #9e9e9e;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alerta-card-item--mas {
  border-style: dashed;
}

.alerta-card-accion {
  display: flex;
  align-items: center;
  margin-top: 12px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64b5f6;
}
</style>
